<template>
  <div v-if="broker != null && user != null">

    <PageHeader v-bind:label="isUserThisBroker ? 'My Details' : broker.first_name + ' ' + broker.last_name + ' Details'"></PageHeader>

    <div class="my-8">

      <div class="flex flex-wrap">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="First name"
            propertyKey="first_name"
            objectType="user"
            v-bind:value="user.first_name"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Last name"
            propertyKey="last_name"
            objectType="user"
            v-bind:value="user.last_name"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Email"
            propertyKey="email"
            objectType="user"
            v-bind:value="user.email"
            v-bind:updateValue="updateValue" />

          <div v-if="emailError != null" class="mt-2 text-red-600">{{ emailError }}</div>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="Company name"
            propertyKey="company"
            objectType="broker"
            v-bind:value="broker.company"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Address line 1"
            propertyKey="address1"
            objectType="broker"
            v-bind:value="broker.address1"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Address line 2"
            propertyKey="address2"
            objectType="broker"
            v-bind:value="broker.address2"
            v-bind:updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="City"
            propertyKey="city"
            objectType="broker"
            v-bind:value="broker.city"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPostcode
            label="Postcode"
            propertyKey="postcode"
            objectType="broker"
            v-bind:value="broker.postcode"
            v-bind:updateValue="updateValue"></InputPostcode>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <StateSelector
            propertyKey="state_id"
            objectType="broker"
            v-bind:value="broker.state_id"
            v-bind:updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <CountrySelector
            propertyKey="country_id"
            objectType="broker"
            v-bind:value="broker.country_id"
            v-bind:updateValue="updateValue"></CountrySelector>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPhone
            label="Office phone"
            propertyKey="phone_office"
            objectType="broker"
            v-bind:value="broker.phone_office"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputPhone
            label="Mobile phone"
            propertyKey="phone_mobile"
            objectType="broker"
            v-bind:value="broker.phone_mobile"
            v-bind:updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPhone
            label="Fax"
            propertyKey="fax"
            objectType="broker"
            v-bind:value="broker.fax"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <InputText
            label="Website"
            propertyKey="url"
            objectType="broker"
            v-bind:value="broker.url"
            v-bind:updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0">
          <div class="label-standard">Credit representative number</div>

          <div class="flex flex-wrap mt-2">
            <div class="w-1/3 pr-2">
              <select class="select-standard"
                v-bind:value="broker.crnType"
                v-on:change="updateValue({
                  objectType: 'broker',
                  key       : 'crnType',
                  value     : $event.target.value
                })">
                <option value="0" selected="selected">ACR</option>
                <option value="1">ACL</option>
              </select>
            </div>

            <div class="w-2/3">
              <InputText
                propertyKey="crn"
                objectType="broker"
                v-bind:value="broker.crn"
                v-bind:updateValue="updateValue" />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <div class="label-standard">
            Aggregator
          </div>

          <div class="form-line">
            <select class="select-standard"
              v-bind:value="broker.aggregator_id == null ? -1 : broker.aggregator_id"
              v-on:change="updateValue({
                objectType: 'broker',
                key       : 'aggregator_id',
                value     : $event.target.value
              })">
              <option value="-1" disabled="">Select an option...</option>
              <option value="1">Australian Finance Group Limited</option>
              <option value="2">Centrepoint Lending Solutions Pty Ltd</option>
              <option value="3">Choice Aggregation Services</option>
              <option value="4">Citiwide Mortgage Services</option>
              <option value="5" selected="selected">Connective Group Pty Ltd</option>
              <option value="6">Crediflex Group Pty Ltd</option>
              <option value="7">Custom Equity Group</option>
              <option value="8">eChoice Home Loans Pty Ltd</option>
              <option value="9">Fast Broker Group</option>
              <option value="10">Fasttrack Finance Group Pty Ltd</option>
              <option value="11">Finsure Finance &amp; Insurance Pty Ltd</option>
              <option value="12">Frontrunner Mortgage Group Pty Ltd</option>
              <option value="13">Lawform Financial Services Group</option>
              <option value="14">Liberty Network Services Pty Ltd</option>
              <option value="15">Loan Market</option>
              <option value="16">Mortgage Choice Limited</option>
              <option value="17">Mortgage House Pty Limited</option>
              <option value="18">Mortgage Point</option>
              <option value="19">My Local Broker</option>
              <option value="20">National Brokers Group Pty Limited</option>
              <option value="21">National Finance Choice Pty Ltd</option>
              <option value="22">National Mortgage Brokers Pty Ltd</option>
              <option value="23">Outsource Financial Pty Ltd</option>
              <option value="24">PLAN Australia</option>
              <option value="25">Reva Broking Pty Ltd</option>
              <option value="26">Residential Property Mortgages Pty Ltd</option>
              <option value="27">Smartline Personal Mortgage Advisers</option>
              <option value="28">Specialist Finance Group</option>
              <option value="29">Vow Financial Group Pty Ltd</option>
              <option value="30">Yellow Brick Road</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div v-if="showMercuryOption" class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="Mercury username"
            propertyKey="lodge_user"
            objectType="broker"
            v-bind:value="broker.lodge_user"
            v-bind:updateValue="updateValue" />
        </div>

        <div v-if="showMercuryOption" class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPassword
            label="Mercury password"
            propertyKey="lodge_pass"
            objectType="broker"
            v-bind:value="broker.lodge_pass"
            v-bind:updateValue="updateValue"></InputPassword>
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div v-if="showMercuryOption" class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="Mercury API key"
            propertyKey="mercury_api_key"
            objectType="broker"
            v-bind:value="broker.mercury_api_key"
            v-bind:updateValue="updateValue" />
        </div>

        <div v-if="showMercuryOption" class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputText
            label="Mercury API token"
            propertyKey="mercury_api_token"
            objectType="broker"
            v-bind:value="broker.mercury_api_token"
            v-bind:updateValue="updateValue" />
        </div>
      </div>
    </div>
    <AutoSaveLoader v-bind:autoSaving="autoSaving" />
  </div>
</template>

<script>
  import StateSelector    from '@/shared/components/form/StateSelector'
  import InputText        from '@/shared/components/form/InputText'
  import InputPassword    from '@/shared/components/form/InputPassword'
  import InputPhone       from '@/shared/components/form/InputPhone'
  import InputPostcode    from '@/shared/components/form/InputPostcode'
  import CountrySelector  from '@/shared/components/form/CountrySelector'
  import PageHeader       from '@/shared/components/headers/PageHeader'
  import { EventBus }     from '@/shared/event-bus';
  import AutoSaveLoader   from '@/shared/components/loaders/AutoSaveLoader'

  export default {

    components: {
      StateSelector,
      InputText,
      InputPassword,
      InputPostcode,
      InputPhone,
      CountrySelector,
      PageHeader,
      AutoSaveLoader
    },

    data() {
      return {
        loading   : false,
        autoSaving: false,
        broker    : null,
        user      : null,

        emailError : null
      }
    },

    watch: {
      '$route' (to, from) {
        this.prepare()
      }
    },

    created() {
      this.prepare()
    },

    mounted() {
      EventBus.$on('set-autosave', trueOrFalse => {
        this.autoSaving = trueOrFalse
      });
    },

    destroyed() {
      EventBus.$off('set-autosave')
    },

    methods : {
      
      prepare() {
        // am I looking my own?
        if(this.$store.state[this.$store.state.view + 'Module'].broker.id == this.$route.params.brokerId) {
          this.user   = _.cloneDeep(this.$store.state.user)
          this.broker = _.cloneDeep(this.$store.state[this.$store.state.view + 'Module'].broker)
        }

        else if(this.isUserAdmin()) {
          this.apiGet({
            brokerId : this.$route.params.brokerId
          }, 'broker/detail', (broker) => {
            this.broker = broker


            this.apiGet({
              userId : broker.user_id
            }, 'user/detail', (user) => {
              this.hideLoader()
              this.user = user
            })
          })
        }
      },

      updateValue(params) {
        this.emailError = null

        if(params.objectType == 'user') {
          this.updateUser(this.user, params.key, params.value, (res) => {
            if(res != null && res.error != null) {
              this.emailError = res.error
            }
          })
        }

        else if(params.objectType == 'broker') {
          this.updateBroker(this.broker, params.key, params.value)
        }
      },
    },
  }
</script>